import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import { urlSyncEffect } from "recoil-sync";
import { string, number, nullable } from "@recoiljs/refine";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { axios } from "../lib/request";
import { dashboardQueryKey } from "../lib/refresh";
import { CHALLENGE_DEFAULT_HISTORY, MAX_PER_PAGE } from "../consts/request";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../lib/error";

export const categoryState = atom({
  key: "games/my/category",
  default: null,
  effects: [
    urlSyncEffect({
      refine: nullable(string()),
      history: 'push'
    })
  ],
});

export const historyState = atom({
  key: "games/my/history",
  default: CHALLENGE_DEFAULT_HISTORY,
});

export const pageState = atom({
  key: "games/my/page",
  default: 1,
  effects: [
    urlSyncEffect({
      refine: number(),
      history: 'push'
    })
  ],
});

export const pageSizeState = atom({
  key: "games/my/pageSize",
  default: MAX_PER_PAGE,
  effects: [
    urlSyncEffect({
      refine: number(),
      history: 'push'
    })
  ],
});

export const termState = atom({
  key: "games/my/term",
  default: "",
  effects: [
    urlSyncEffect({
      refine: string(),
      history: 'push'
    })
  ],
});

/**
 * Fetches the paginated list of games for the dashboard.
 * 
 * @param {string|null} category - The category filter.
 * @param {number} history - The history filter (days).
 * @param {number} page - The current page number.
 * @param {number} per_page - The number of items per page.
 * @param {string} term - The search term.
 * @returns {Promise<Object>} The games payload including metadata.
 */
export const fetchGames = async (category, history, page, per_page, term) => {
  const url = process.env.REACT_APP_API_GAMES_URL;
  const response = await axios.get(url, {
    params: { category, history, page, per_page, term },
  });
  const { data } = response || {}
  return data;
};

export const useDashboardQueryKey = () => {
  const category = useRecoilValue(categoryState);
  const history = useRecoilValue(historyState);
  const page = useRecoilValue(pageState);
  const pageSize = useRecoilValue(pageSizeState);
  const term = useRecoilValue(termState);
  return dashboardQueryKey(category, history, page, pageSize, term);
}

/**
 * A React Query hook that fetches the user's games based on the current Recoil state 
 * (category, history, page, pageSize, search term).
 * 
 * Uses `suspense: true` to trigger the loading placeholder and handles global error alerts.
 * 
 * @returns {import("react-query").UseQueryResult} The query result object.
 */
export const useGamesDataQuery = () => {
  const queryKey = useDashboardQueryKey()
  const category = useRecoilValue(categoryState);
  const history = useRecoilValue(historyState);
  const page = useRecoilValue(pageState);
  const pageSize = useRecoilValue(pageSizeState);
  const term = useRecoilValue(termState);
  const setAlert = useSetRecoilState(alertState);
  return useQuery(
    queryKey,
    () => fetchGames(category, history, page, pageSize, term),
    { 
      keepPreviousData: true, 
      suspense: true,
      useErrorBoundary: false,
      onError: (error) => {
        const message = getErrorMessage(error);
        setAlert({ type: "error", message, closable: true });
      }
    }
  );
};

export const useGamesQuery = () => {
  const { data } = useGamesDataQuery();
  return data?.games || [];
};

export const useGamesFetchingQuery = () => {
  const { isFetching } = useGamesDataQuery();
  return isFetching;
};

export const usePagesQuery = () => {
  const { data } = useGamesDataQuery();
  return data?.meta?.pages || 0;
};

export const useLiberateMutation = (game_id) => {
  const queryClient = useQueryClient();

  const { isLoading, mutateAsync } = useMutation(
    ({ params }) =>
      axios.patch(
        `https://app.socialscavenger.com/v1/games/${game_id}/liberate.json`,
        params,
      ),
    {
      onError: (error) => console.log("[error]", error),
      onSuccess: () => queryClient.invalidateQueries(dashboardQueryKey()),
    }
  );

  return { isLiberating: isLoading, liberate: mutateAsync };
};

export const useCollectReviewsMutation = (game_id) => {
  const setAlert = useSetRecoilState(alertState);
  const { isLoading, mutateAsync } = useMutation(
    () =>
      axios.post(
        `${process.env.REACT_APP_API_GAMES_RAW_URL}/${game_id}/reviews.json`,
        {
            game_id,
        },
      ),
    {
      onError: (error) => console.log("[error]", error),
      onSuccess: () => {
        setAlert({
            type: "info",
            message:
              "Game reviews have been requested.",
        });
      },
    }
  );

  return { isRequestingReviews: isLoading, requestReviews: mutateAsync };
};
